import { useState } from "react";
import styles from "./style.module.scss";
import { Switch } from "antd";

export interface SettingEntry {
  key: string;
  description: string;
  category: string;
  value_type: "bool" | "int" | "float" | "str" | "list" | "unknown";
  current_value: any;
  original_value: any | null;
  is_overridden: boolean;
  exists?: boolean;
}

interface Props {
  setting: SettingEntry;
  onToggle: (key: string, value: any) => void;
  onRevert: (key: string) => void;
}

function ListEditorModal({
  settingKey,
  items,
  onSave,
  onClose,
}: {
  settingKey: string;
  items: string[];
  onSave: (newItems: string[]) => void;
  onClose: () => void;
}) {
  const [localItems, setLocalItems] = useState<string[]>([...items]);
  const [inputValue, setInputValue] = useState("");

  const hasChanges =
    localItems.length !== items.length ||
    localItems.some((item, i) => item !== items[i]);

  const addItem = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !localItems.includes(trimmed)) {
      setLocalItems([...localItems, trimmed]);
      setInputValue("");
    }
  };

  const removeItem = (item: string) => {
    setLocalItems(localItems.filter((i) => i !== item));
  };

  const handleDone = () => {
    if (hasChanges) {
      onSave(localItems);
    }
    onClose();
  };

  return (
    <div className={styles.listEditorOverlay} onClick={handleDone}>
      <div
        className={styles.listEditorPanel}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.listEditorHeader}>
          <h3>{settingKey}</h3>
          <button className={styles.listEditorClose} onClick={handleDone}>
            x
          </button>
        </div>
        <div className={styles.listEditorBody}>
          {localItems.length === 0 ? (
            <div className={styles.emptyList}>No items — add one below</div>
          ) : (
            <div className={styles.chips}>
              {localItems.map((item) => (
                <span key={item} className={styles.chip}>
                  {item}
                  <button
                    className={styles.chipRemove}
                    onClick={() => removeItem(item)}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className={styles.chipInputRow}>
            <input
              className={styles.chipInput}
              type="text"
              placeholder="Add value..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addItem();
                }
              }}
              autoFocus
            />
            <button
              className={styles.chipAddBtn}
              onClick={addItem}
              disabled={!inputValue.trim()}
            >
              Add
            </button>
          </div>
          <div className={styles.listEditorFooter}>
            <button className={styles.doneBtn} onClick={handleDone}>
              {hasChanges ? "Save & Close" : "Close"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SettingRow({ setting, onToggle, onRevert }: Props) {
  const {
    key,
    description,
    value_type,
    current_value,
    is_overridden,
    original_value,
    exists = true,
  } = setting;

  const [showListEditor, setShowListEditor] = useState(false);

  if (!exists) {
    return (
      <div className={`${styles.settingRow} ${styles.notFound}`}>
        <span className={styles.key}>{key}</span>
        <span className={styles.description}>Not found in Settings</span>
        <span className={styles.control} />
        <span className={styles.actions} />
      </div>
    );
  }

  const isList = value_type === "list";
  const listItems = isList && Array.isArray(current_value) ? current_value : [];

  const renderControl = () => {
    if (value_type === "bool") {
      return (
        <Switch
          defaultChecked={Boolean(current_value)}
          onChange={() => onToggle(key, !current_value)}
        />
      );
    }
    if (isList) {
      return (
        <>
          <button
            className={styles.listEditBtn}
            onClick={() => setShowListEditor(true)}
          >
            Edit list
          </button>
          <span className={styles.listCount}>
            {listItems.length} item{listItems.length !== 1 ? "s" : ""}
          </span>
        </>
      );
    }
    return (
      <input
        key={String(current_value)}
        className={styles.valueInput}
        type={
          value_type === "int" || value_type === "float" ? "number" : "text"
        }
        defaultValue={String(current_value ?? "")}
        onBlur={(e) => {
          const newVal = e.target.value;
          if (String(current_value) !== newVal) {
            onToggle(key, newVal);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
    );
  };

  return (
    <>
      <div
        className={`${styles.settingRow} ${is_overridden ? styles.overridden : ""}`}
      >
        <span className={styles.key}>
          {key}
          {is_overridden && (
            <span
              className={styles.overrideDot}
              title={`Original: ${Array.isArray(original_value) ? original_value.join(", ") : original_value}`}
            />
          )}
        </span>
        <span className={styles.description}>{description}</span>
        <span className={styles.control}>{renderControl()}</span>
        <span className={styles.actions}>
          {is_overridden && (
            <button className={styles.revertBtn} onClick={() => onRevert(key)}>
              Revert
            </button>
          )}
        </span>
      </div>
      {showListEditor && (
        <ListEditorModal
          settingKey={key}
          items={listItems}
          onSave={(newItems) => onToggle(key, newItems)}
          onClose={() => setShowListEditor(false)}
        />
      )}
    </>
  );
}
